import { gql } from '@apollo/client'

const DOCUMENT_ATTRIBUTES = gql`
  fragment documentAttributes on Document {
    id
    createdAt
    updatedAt
    archivedAt
    name
    summary
    status
    type
    url
    public
    category
    subcontractor {
      id
      company {
        name
      }
      contacts {
        id
        name
        email
        number
        isPrimaryContact
      }
    }
    project {
      id
      name
      status
      integrationConnections {
        integration {
          integrationType
        }
      }
    }
    builder {
      id
      company {
        name
      }
    }
    currentVersion {
      version
    }
    versionHistory {
      version
      document {
        id
        name
        status
        createdAt
        archivedAt
      }
    }
  }
`

export const DOCUMENT_ATTRIBUTES_SUBSET = gql`
  fragment documentAttributesSubset on Document {
    id
    createdAt
    name
    status
    type
    url
    public
    subcontractor {
      id
      company {
        name
      }
      contacts {
        id
        name
        email
        number
        isPrimaryContact
      }
    }
    project {
      id
      name
    }
  }
`

export const CREATE_DOCUMENT = gql`
  mutation createDocument_UI($input: CreateDocumentInput!) {
    createDocument(input: $input) {
      ...documentAttributes
    }
  }
  ${DOCUMENT_ATTRIBUTES}
`

export const CREATE_DOCUMENTS = gql`
  mutation createDocuments_UI($input: [CreateDocumentInput!]!) {
    createDocuments(input: $input) {
      ...documentAttributes
    }
  }
  ${DOCUMENT_ATTRIBUTES}
`

export const GET_DOCUMENTS = gql`
  query documents_UI($filter: DocumentFilterInput) {
    documents(filter: $filter) {
      ...documentAttributes
    }
  }
  ${DOCUMENT_ATTRIBUTES}
`

// Subset of Documents data, used for fast list view
export const GET_DOCUMENTS_LIST = gql`
  query documentsList_UI(
    $filter: DocumentFilterInput
    $sort: DocumentSortInput
    $pagination: PaginationInput
  ) {
    documents(filter: $filter, sort: $sort, pagination: $pagination) {
      ...documentAttributesSubset
    }
    documentsAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${DOCUMENT_ATTRIBUTES_SUBSET}
`

export const GET_DOCUMENTS_LIST_WITH_FOLDERS = gql`
  query documentsListWithFolders_UI(
    $filter: DocumentFilterInput
    $sort: DocumentSortInput
    $pagination: PaginationInput
    $folderFilter: DocumentFolderFilterInput!
    $folderSort: DocumentFolderSortInput
  ) {
    documents(filter: $filter, sort: $sort, pagination: $pagination) {
      ...documentAttributesSubset

      currentVersion {
        version
      }
      versionHistory {
        version
      }
    }
    documentsAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
    documentFolders(filter: $folderFilter, sort: $folderSort) {
      id
      name
    }
  }
  ${DOCUMENT_ATTRIBUTES_SUBSET}
`

export const GET_DOCUMENT = gql`
  query document_UI($id: String!) {
    document(id: $id) {
      ...documentAttributes
    }
  }
  ${DOCUMENT_ATTRIBUTES}
`

export const UPDATE_DOCUMENT = gql`
  mutation updateDocument_UI(
    $id: String!
    $name: String
    $type: String
    $status: DocumentStatus
    $summary: String
    $key: String
    $public: Boolean
  ) {
    updateDocument(
      id: $id
      input: {
        name: $name
        type: $type
        status: $status
        summary: $summary
        key: $key
        public: $public
      }
    ) {
      ...documentAttributes
    }
  }
  ${DOCUMENT_ATTRIBUTES}
`

export const UPDATE_DOCUMENT_STATUS = gql`
  mutation updateDocumentStatus_UI($id: String!, $status: DocumentStatus) {
    updateDocument(id: $id, input: { status: $status }) {
      ...documentAttributes
    }
  }
  ${DOCUMENT_ATTRIBUTES}
`

export const GET_DOCUMENT_UPLOAD_URL = gql`
  query getPresignedUrlForDocument_UI(
    $id: String!
    $filename: String!
    $filesize: String!
    $filetype: String!
  ) {
    getPresignedUrl(
      input: {
        id: $id
        type: DOCUMENT
        filename: $filename
        filesize: $filesize
        filetype: $filetype
      }
    ) {
      url
      key
    }
  }
`

export const DELETE_DOCUMENT = gql`
  mutation deleteDocument_UI($id: String!) {
    deleteDocument(id: $id) {
      id
    }
  }
`

export const SEND_REQUEST_UPDATE_EMAIL = gql`
  mutation sendRequestUpdateEmail_UI(
    $documentType: DocumentType!
    $documentId: String!
    $subcontractorId: String!
    $customMessage: String
    $contactIds: [String!]!
  ) {
    sendRequestUpdateEmail(
      input: {
        documentType: $documentType
        documentId: $documentId
        subcontractorId: $subcontractorId
        customMessage: $customMessage
        contactIds: $contactIds
      }
    ) {
      serviceResponse
    }
  }
`

export const ARCHIVE_DOCUMENT = gql`
  mutation archiveDocument_UI($id: String!) {
    archiveDocument(id: $id) {
      id
    }
  }
`

export const RESTORE_DOCUMENT = gql`
  mutation restoreDocument_UI($id: String!) {
    restoreDocument(id: $id) {
      id
    }
  }
`

export const BULK_ARCHIVE_DOCUMENTS = gql`
  mutation bulkArchiveDocuments_UI($ids: [String!]!) {
    bulkArchiveDocuments(ids: $ids)
  }
`

export const BULK_RESTORE_DOCUMENTS = gql`
  mutation bulkRestoreDocuments_UI($ids: [String!]!) {
    bulkRestoreDocuments(ids: $ids)
  }
`

export const GET_DOCUMENT_FOLDER_HIERARCHY = gql`
  query documentFolderHierarchy_UI($id: String!) {
    documentFolderHierarchy(id: $id) {
      id
      name
    }
  }
`

export const CREATE_DOCUMENT_FOLDER = gql`
  mutation createDocumentFolder_UI($input: CreateDocumentFolderInput!) {
    createDocumentFolder(input: $input) {
      id
      name
    }
  }
`

export const UPDATE_DOCUMENT_FOLDER = gql`
  mutation updateDocumentFolder_UI(
    $id: String!
    $input: UpdateDocumentFolderInput!
  ) {
    updateDocumentFolder(id: $id, input: $input) {
      id
      name
    }
  }
`

export const GET_DOCUMENT_FOLDER_TREE = gql`
  query documentFolderTree_UI(
    $category: DocumentCategory!
    $projectId: String
  ) {
    documentFolderTree(category: $category, projectId: $projectId) {
      id
      name
      parentId
    }
  }
`

export const MOVE_DOCUMENTS = gql`
  mutation moveDocuments_UI($ids: [String!]!, $documentFolderId: String) {
    moveDocuments(ids: $ids, documentFolderId: $documentFolderId) {
      id
    }
  }
`

export const MOVE_DOCUMENT_FOLDERS = gql`
  mutation moveDocumentFolders_UI($ids: [String!]!, $documentFolderId: String) {
    moveDocumentFolders(ids: $ids, documentFolderId: $documentFolderId) {
      id
    }
  }
`

export const IS_DOCUMENT_FOLDER_EMPTY = gql`
  query isDocumentFolderEmpty_UI($id: String!) {
    isDocumentFolderEmpty(id: $id)
  }
`

export const DELETE_DOCUMENT_FOLDER = gql`
  mutation deleteDocumentFolder_UI($id: String!) {
    deleteDocumentFolder(id: $id) {
      id
    }
  }
`

const DOCUMENT_ENTITY_ATTRIBUTES = gql`
  fragment documentEntityAttributes on DocumentEntity {
    id
    url
    name
  }
`

export const GET_DOCUMENT_FOR_WORKER = gql`
  query documentForWorker_UI($id: String!) {
    documentForWorker(id: $id) {
      ...documentEntityAttributes
    }
  }
  ${DOCUMENT_ENTITY_ATTRIBUTES}
`

export const GET_WORKER_DOCUMENTS = gql`
  query getWorkerDocuments_UI($projectId: String!, $subcontractorId: String) {
    workerDocuments(projectId: $projectId, subcontractorId: $subcontractorId) {
      swmsDocs {
        id
        updatedAt
        name
        url
        isBuilder
        required
        requiredAll
        showAll
      }
      msdsDocs {
        id
        updatedAt
        name
        url
      }
      documents {
        id
        updatedAt
        name
        category
        url
        type
      }
    }
  }
`

export const GET_VISITOR_DOCUMENTS = gql`
  query documents_visitor_UI($projectId: String!) {
    documents_visitor(projectId: $projectId) {
      swmsDocs {
        id
        updatedAt
        name
        url
        isBuilder
        required
        requiredAll
        showAll
      }
      msdsDocs {
        id
        updatedAt
        name
        url
      }
      documents {
        id
        updatedAt
        name
        category
        url
        type
      }
    }
  }
`

export const GET_VISITOR_SWMS = gql`
  query swmsDoc_visitor_UI($projectId: String!, $documentId: String!) {
    swmsDoc_visitor(documentId: $documentId, projectId: $projectId) {
      name
      url
    }
  }
`

export const GET_VISITOR_MSDS = gql`
  query msdsDoc_visitor_UI($projectId: String!, $documentId: String!) {
    msdsDoc_visitor(documentId: $documentId, projectId: $projectId) {
      name
      url
    }
  }
`

export const GET_VISITOR_DOCUMENT = gql`
  query document_visitor_UI($projectId: String!, $documentId: String!) {
    document_visitor(documentId: $documentId, projectId: $projectId) {
      id
      name
      category
      url
      type
    }
  }
`
